import {
  PROJECT_VISIBILITY_VALUES,
  schema,
} from '@edgevanta/db/drizzle/client';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';
import { ANALYSIS_JOB_TYPES } from '../pipelines/analysis-job-types';
import { createQuerySchemaWithPagination } from '../shared';

export const selectProjectSchema = createSelectSchema(schema.projects, {
  visibility: z.enum(PROJECT_VISIBILITY_VALUES),
});

// Document Analysis owns these two columns; only its own repository writes them.
export const ANALYSIS_PROJECT_COLUMNS = {
  analysisFolderId: true,
  analysisArchivedAt: true,
} as const;

export type AnalysisProjectColumn = keyof typeof ANALYSIS_PROJECT_COLUMNS;

// Written by createAnalysisProject and nothing else, so they are kept out of the
// ordinary create input rather than merely left unset by it.
export const ANALYSIS_CREATE_COLUMNS = {
  analysisEnabledAt: true,
  analysisContractNumber: true,
  analysisJobType: true,
} as const;

export const insertProjectSchema = createInsertSchema(schema.projects, {
  description: z.string().max(2048).optional(),
  name: z.string().max(255).min(1),
  visibility: z.enum(PROJECT_VISIBILITY_VALUES),
}).omit({
  organizationId: true,
  ...ANALYSIS_PROJECT_COLUMNS,
  ...ANALYSIS_CREATE_COLUMNS,
});

/** The Document Analysis create screen: a name, and the two package facts the
 *  run is later stamped with. Both facts are optional. */
export const createAnalysisProjectSchema = z.object({
  name: z.string().trim().min(1).max(255),
  contractNumber: z.string().trim().max(64).nullish(),
  jobType: z.enum(ANALYSIS_JOB_TYPES).nullish(),
});

export const updateProjectSchema = insertProjectSchema.partial().extend({
  id: z.string(),
  name: z.string().trim().min(1).max(255).optional(),
  organizationId: z.string().uuid(),
});

/** Create a folder for grouping this org's projects (EDG-1886). */
export const createProjectFolderSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2048).nullish(),
  /** The folder to create this one inside. Absent or null is the top level. */
  parentId: z.string().uuid().nullish(),
});

export const updateProjectFolderSchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2048).nullish(),
    /** Where the folder now sits. `null` moves it to the top level. */
    parentId: z.string().uuid().nullish(),
  })
  .refine(
    (input) =>
      input.name !== undefined ||
      input.description !== undefined ||
      input.parentId !== undefined,
    'At least one field is required',
  );

/** Put a project in a folder, or take it out of one with `null`. */
export const setProjectFolderSchema = z.object({
  folderId: z.string().uuid().nullable(),
});

/** Put many projects in one folder, or take them all out with `null`.
 *  `folderId` is nullable but NOT optional, same as the single-row schema: an
 *  omitted key is a malformed request, never an unfile. */
export const bulkSetProjectFolderSchema = z.object({
  projectIds: z.array(z.string().uuid()).min(1).max(200),
  folderId: z.string().uuid().nullable(),
});

/** The folder ids in the order the user arranged them. */
export const reorderProjectFoldersSchema = z.object({
  folderIds: z.array(z.string().uuid()),
});

/** Which folder the list is looking inside. A uuid is that folder, `none` is
 *  the top of the list, and leaving it out is every project whatever its
 *  folder — which is what a search across the whole tree needs. */
export const PROJECT_FOLDER_QUERY_NONE = 'none';

const ProjectsQuerySchema = z.object({
  search: z.string().optional(),
  name: z.string().optional(),
  userId: z.string().uuid(),
  folderId: z
    .union([z.string().uuid(), z.literal(PROJECT_FOLDER_QUERY_NONE)])
    .optional(),
  visibility: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((visibility) => visibility);
    })
    .default([]),
  createdAt: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((num) => Number(num));
    })
    .default([]),
});

// Newest first, so scrolling the list walks back in time and the pages that
// load read as older projects rather than as a second alphabet.
export const PROJECTS_DEFAULT_SORT = { id: 'createdAt', desc: true } as const;

export const getProjectsQuerySchema = createQuerySchemaWithPagination(
  ProjectsQuerySchema,
  { ...PROJECTS_DEFAULT_SORT },
).merge(
  z.object({
    limit: z.coerce.number().optional().default(10),
  }),
);

export const getProjectsCountQuerySchema = ProjectsQuerySchema;
